// The drift report turned around: one row per application instead of one per machine. Pure functions, no React, no requests: the report
// the API already sends (each machine with its findings) is grouped by the program's key.
import { gap, hasJob, list } from './driftShared';

// A wrong machine id belongs to a machine, not to an application.
const NOT_AN_APPLICATION = ['identityMismatch'];

// The name an application is shown under: the catalog's name for it (the API sends it as displayName), then the policy's name when there is
// one (a missing program has only that), otherwise the shortest name a machine reports ("Git", not "Git version 2.50.0").
const displayName = (key, findings) => {
  const fromCatalog = findings.find((f) => f.displayName);
  if (fromCatalog) return fromCatalog.displayName;
  const fromPolicy = findings.find((f) => f.gap === 'missing' && f.name);
  if (fromPolicy) return fromPolicy.name;
  const names = findings.map((f) => f.name).filter(Boolean).sort((a, b) => a.length - b.length || a.localeCompare(b));
  return names[0] || key;
};

const newestFirst = (a, b) => b.localeCompare(a, undefined, { numeric: true });

// [{ key, name, available, machineCount, worst, gaps: [{ gap, entries: [{ machine, finding }] }], versions: [{ version, count }] }]
// worst is the rank of the most serious finding; the list is sorted by it, then by how many machines are affected.
export const byApplication = (machines) => {
  const apps = new Map();
  list(machines).forEach((machine) => {
    list(machine.findings).forEach((finding) => {
      if (!finding?.key || NOT_AN_APPLICATION.includes(finding.gap)) return;
      if (!apps.has(finding.key)) apps.set(finding.key, { key: finding.key, findings: [], gaps: new Map(), machineIds: new Set(), versions: new Map() });
      const app = apps.get(finding.key);
      const id = machine.deviceId || machine.computer;
      const entry = { machine: { deviceId: machine.deviceId, computer: machine.computer || machine.deviceId, id }, finding };
      app.findings.push(finding);
      app.machineIds.add(id);
      if (!app.gaps.has(finding.gap)) app.gaps.set(finding.gap, []);
      app.gaps.get(finding.gap).push(entry);
      if (finding.installed) app.versions.set(finding.installed, (app.versions.get(finding.installed) || 0) + 1);
    });
  });

  return [...apps.values()].map((app) => {
    const gaps = [...app.gaps.entries()]
      .map(([g, entries]) => ({ gap: g, entries: entries.sort((a, b) => a.machine.computer.localeCompare(b.machine.computer)) }))
      .sort((a, b) => gap(a.gap).rank - gap(b.gap).rank);
    return {
      key: app.key,
      name: displayName(app.key, app.findings),
      wingetId: (app.findings.find((f) => f.wingetId) || {}).wingetId || null,
      available: (app.findings.find((f) => f.available) || {}).available || null,
      machineCount: app.machineIds.size,
      worst: gaps.length ? gap(gaps[0].gap).rank : 99,
      gaps,
      versions: [...app.versions.entries()].map(([version, count]) => ({ version, count })).sort((a, b) => b.count - a.count || newestFirst(a.version, b.version)),
    };
  }).sort((a, b) => a.worst - b.worst || b.machineCount - a.machineCount || a.name.localeCompare(b.name));
};

// "156.0.8078 ×5, 155.0.8059 ×2 +1 more": the versions installed across the machines, the most common first.
export const versionsText = (versions, show = 3) => {
  const shown = list(versions).slice(0, show).map((v) => `${v.version} ×${v.count}`).join(', ');
  const more = list(versions).length - show;
  return more > 0 ? `${shown} +${more} more` : shown;
};

// The entries of one kind of finding that can be queued (the API proposes a script for them).
export const queueable = (entries) => list(entries).filter((e) => hasJob(e.finding));

// The applications every machine that has them is in line on: no machine has a finding for them. The machines report them as compliant programs
// (okItems, state "installed"); a program that is absent as the policy wants it (a forbidden one) is not an application worth listing.
// [{ key, name, wingetId, available, mode, machineCount, inLine: true, gaps: [], entries: [{ machine, installed }], versions: [{ version, count }] }], by name.
export const inLineApplications = (machines) => {
  const flagged = new Set();
  list(machines).forEach((machine) => list(machine.findings).forEach((finding) => {
    if (finding?.key && !NOT_AN_APPLICATION.includes(finding.gap)) flagged.add(finding.key);
  }));
  const apps = new Map();
  list(machines).forEach((machine) => list(machine.okItems).forEach((item) => {
    if (!item?.key || item.state !== 'installed' || flagged.has(item.key)) return;
    if (!apps.has(item.key)) apps.set(item.key, { key: item.key, name: item.displayName || item.key, wingetId: item.wingetId || null, available: item.available || null, mode: item.mode || null, entries: [], versions: new Map() });
    const app = apps.get(item.key);
    const id = machine.deviceId || machine.computer;
    app.entries.push({ machine: { deviceId: machine.deviceId, computer: machine.computer || machine.deviceId, id }, installed: item.installed || '' });
    String(item.installed || '').split(', ').filter(Boolean).forEach((version) => app.versions.set(version, (app.versions.get(version) || 0) + 1));
  }));
  return [...apps.values()].map((app) => ({
    key: app.key, name: app.name, wingetId: app.wingetId, available: app.available, mode: app.mode, inLine: true, gaps: [], machineCount: app.entries.length,
    entries: app.entries.sort((a, b) => a.machine.computer.localeCompare(b.machine.computer)),
    versions: [...app.versions.entries()].map(([version, count]) => ({ version, count })).sort((a, b) => b.count - a.count || newestFirst(a.version, b.version)),
  })).sort((a, b) => a.name.localeCompare(b.name));
};
